import invariant from 'tiny-invariant'
import { ChainId } from '../constants'
import { validateAndParseAddress } from '../utils'
import { Currency } from './currency'

/**
 * Represents an ERC20 token with a unique address and some metadata.
 */
export class Token extends Currency {
  public readonly chainId: ChainId
  public readonly address: string

  public constructor(chainId: ChainId, address: string, decimals: number, symbol?: string, name?: string) {
    super(decimals, symbol, name)
    this.chainId = chainId
    this.address = validateAndParseAddress(address)
  }

  /**
   * Returns true if the two tokens are equivalent, i.e. have the same chainId and address.
   * @param other other token to compare
   */
  public equals(other: Token): boolean {
    // short circuit on reference equality
    if (this === other) {
      return true
    }
    return this.chainId === other.chainId && this.address === other.address
  }

  /**
   * Returns true if the address of this token sorts before the address of the other token
   * @param other other token to compare
   * @throws if the tokens have the same address
   * @throws if the tokens are on different chains
   */
  public sortsBefore(other: Token): boolean {
    invariant(this.chainId === other.chainId, 'CHAIN_IDS')
    invariant(this.address !== other.address, 'ADDRESSES')
    return this.address.toLowerCase() < other.address.toLowerCase()
  }
}

/**
 * Compares two currencies for equality
 */
export function currencyEquals(currencyA: Currency, currencyB: Currency): boolean {
  if (currencyA instanceof Token && currencyB instanceof Token) {
    return currencyA.equals(currencyB)
  } else if (currencyA instanceof Token) {
    return false
  } else if (currencyB instanceof Token) {
    return false
  } else {
    return currencyA === currencyB
  }
}

export const WETH = {
  [ChainId.MAINNET]: new Token(ChainId.MAINNET, '0xBb66F8b7FA7c4aA71Fb8bB3795fae15B5d9d8995', 18, 'WETH', 'Wrapped ETH'),
  [ChainId.KTOTEST]: new Token(ChainId.KTOTEST, '0xBb66F8b7FA7c4aA71Fb8bB3795fae15B5d9d8995', 11, 'WKTO', 'Wrapped KTO'),
  [ChainId.KTOMAIN]: new Token(ChainId.KTOMAIN, '0x3850D46C0442A86AaDd41D99f635e316c8788269', 11, 'WKTO', 'Wrapped KTO'),
  [ChainId.NOSMAIN]: new Token(ChainId.NOSMAIN, '0x69c63c002De3589F42EC46FAF4819C6ec7e3e969', 11, 'WKTO', 'Wrapped KTO'),
}
